import { useCallback, useEffect, useRef, useState } from 'react';
import { useNewLocationSystemEnabled } from '../../../utils/facets/new-location-system-dev-flag';
import { buildTeaserFrames, TEASER_SESSION_KEY, TEASER_TIMING } from '../../../utils/search-bar/search-teaser-timeline';
import type { SearchTeaser } from '../../../server/utils/search-teaser';

export interface SearchBarTeaserState {
  /** True while the typewriter is running; the host swaps its placeholder for `text`. */
  active: boolean;
  text: string;
  /** Count for the words typed so far, or null to keep the host's own idle count. */
  count: number | null;
  /** All open jobs, for the narrowing line's denominator. */
  total: number | null;
  /** Interrupt for good (hover, focus, click): the bar returns to idle and won't replay this session. */
  stop: () => void;
}

function readPlayed(): boolean {
  try {
    return window.sessionStorage.getItem(TEASER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markPlayed(): void {
  try {
    window.sessionStorage.setItem(TEASER_SESSION_KEY, '1');
  } catch {
    // Storage blocked: the teaser may replay on the next page, which is harmless.
  }
}

function shouldRun(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (readPlayed()) return false;
  if (document.visibilityState !== 'visible') return false;
  if (!window.matchMedia(`(min-width: ${TEASER_TIMING.minViewportPx}px)`).matches) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return true;
}

/**
 * Drives the closed search bar's typewriter teaser (Sage search bar, 2026-09-28): a couple of
 * sample searches typed word by word, with real per-word counts from GET /api/search-teaser.
 * Runs at most once per session, only on desktop, never with reduced motion, and never while
 * `enabled` is false (the host passes false once the visitor has a query, or the panel is open).
 * Any failure (no counts, fetch error) just leaves the bar idle.
 */
export function useSearchBarTeaser(locale: 'en' | 'fi' | 'sv', enabled: boolean): SearchBarTeaserState {
  const system = useNewLocationSystemEnabled() ? 'new' : 'legacy';
  const [active, setActive] = useState(false);
  const [text, setText] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const stoppedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const stop = useCallback(() => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    clearTimers();
    markPlayed();
    setActive(false);
    setText('');
    setCount(null);
  }, [clearTimers]);

  useEffect(() => {
    if (!enabled) {
      // Not an interruption (the visitor has a query, or the panel is open): just go idle, and
      // leave the session flag alone so a later empty bar can still play it once.
      clearTimers();
      setActive(false);
      setText('');
      setCount(null);
      return;
    }
    if (stoppedRef.current || !shouldRun()) return;
    let cancelled = false;
    const startTimer = setTimeout(() => {
      fetch(`/api/search-teaser?locale=${locale}&ls=${system}`, { headers: { Accept: 'application/json' } })
        .then(async (res) => (res.ok ? ((await res.json()) as { teaser: SearchTeaser | null }).teaser : null))
        .catch(() => null)
        .then((teaser) => {
          if (cancelled || stoppedRef.current || !teaser || teaser.samples.length === 0 || !shouldRun()) return;
          const frames = buildTeaserFrames(teaser.samples);
          if (frames.length === 0) return;
          setTotal(teaser.total);
          setActive(true);
          frames.forEach((frame) => {
            timersRef.current.push(
              setTimeout(() => {
                setText(frame.text);
                setCount(frame.count);
              }, frame.atMs),
            );
          });
          const endAt = frames[frames.length - 1].atMs + TEASER_TIMING.gapMs;
          timersRef.current.push(setTimeout(stop, endAt));
        });
    }, TEASER_TIMING.startDelayMs);
    timersRef.current.push(startTimer);
    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [enabled, locale, system, stop, clearTimers]);

  return { active, text, count, total, stop };
}
